import { useEffect, useState } from 'react'
import { Plus, Building2, Edit, Trash2, Eye } from 'lucide-react'
import { organizationService } from '../../services/organizationService'
import type { Organization } from '../../types'
import { Button } from '../ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card'
import { Badge } from '../ui/badge'

interface OrganizationListProps {
  onSelect?: (org: Organization) => void
  onEdit?: (org: Organization) => void
  onCreate?: () => void
}

export function OrganizationList({ onSelect, onEdit, onCreate }: OrganizationListProps) {
  const [organizations, setOrganizations] = useState<Organization[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    loadOrganizations()
  }, [])

  const loadOrganizations = async () => {
    try {
      setLoading(true)
      const data = await organizationService.getAll()
      setOrganizations(data)
      setError(null)
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to load organizations')
    } finally {
      setLoading(false)
    }
  }

  const handleDelete = async (id: string) => {
    if (!confirm('Are you sure you want to delete this organization?')) return

    try {
      await organizationService.delete(id)
      await loadOrganizations()
    } catch (err: any) {
      alert(err.response?.data?.error || 'Failed to delete organization')
    }
  }

  const getSizeBadge = (size?: string | null) => {
    const colors: Record<string, string> = {
      small: 'bg-blue-100 text-blue-800',
      medium: 'bg-green-100 text-green-800',
      large: 'bg-orange-100 text-orange-800',
      enterprise: 'bg-purple-100 text-purple-800',
    }
    return size ? (
      <Badge className={colors[size] || 'bg-gray-100 text-gray-800'}>
        {size.charAt(0).toUpperCase() + size.slice(1)}
      </Badge>
    ) : null
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center p-8">
        <div className="text-muted-foreground">Loading organizations...</div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
        <p className="text-red-800">{error}</p>
        <Button onClick={loadOrganizations} className="mt-2" variant="outline" size="sm">
          Retry
        </Button>
      </div>
    )
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold">Organizations</h2>
          <p className="text-muted-foreground">Manage your organizations and departments</p>
        </div>
        {onCreate && (
          <Button onClick={onCreate}>
            <Plus className="w-4 h-4 mr-2" />
            New Organization
          </Button>
        )}
      </div>

      {organizations.length === 0 ? (
        <Card>
          <CardContent className="flex flex-col items-center justify-center p-8">
            <Building2 className="w-12 h-12 text-muted-foreground mb-4" />
            <p className="text-muted-foreground mb-4">No organizations yet</p>
            {onCreate && (
              <Button onClick={onCreate}>
                <Plus className="w-4 h-4 mr-2" />
                Create Your First Organization
              </Button>
            )}
          </CardContent>
        </Card>
      ) : (
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
          {organizations.map((org) => (
            <Card key={org.id} className="hover:shadow-lg transition-shadow">
              <CardHeader>
                <div className="flex items-start justify-between">
                  <div className="flex items-center gap-2">
                    <Building2 className="w-5 h-5 text-primary" />
                    <CardTitle className="text-lg">{org.name}</CardTitle>
                  </div>
                  {getSizeBadge(org.size)}
                </div>
                {org.industry && (
                  <CardDescription className="text-sm">{org.industry}</CardDescription>
                )}
              </CardHeader>
              <CardContent>
                {org.description && (
                  <p className="text-sm text-muted-foreground mb-4 line-clamp-2">
                    {org.description}
                  </p>
                )}
                {org.website && (
                  <a
                    href={org.website}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-sm text-primary hover:underline mb-4 block"
                  >
                    {org.website}
                  </a>
                )}
                <div className="flex gap-2 mt-4">
                  {onSelect && (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => onSelect(org)}
                      className="flex-1"
                    >
                      <Eye className="w-4 h-4 mr-1" />
                      View
                    </Button>
                  )}
                  {onEdit && (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => onEdit(org)}
                    >
                      <Edit className="w-4 h-4" />
                    </Button>
                  )}
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handleDelete(org.id)}
                    className="text-red-600 hover:text-red-700"
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  )
}
